import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingColumns, faPen, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { Panel, DashModal, money } from './Dashui';
import { useChartTheme, baseOptions } from './Chartkit';
import './BalanceCard.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const when = (v) =>
  v
    ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Beirut' })
    : '';
const longDay = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const startLabel = (h) => (h?.fromDay ? `the start of ${longDay(h.fromDay)}` : when(h?.asOf));
const todayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Beirut' });
const firstOfMonth = () => `${todayISO().slice(0, 8)}01`;
const shortDay = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/* Account balance: you type what is in the account now; then it follows by itself
   (+ packages marked paid, - expenses added). Used on Finance and Growth. */
const BalanceCard = ({ chart = true }) => {
  const { c } = useChartTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ amount: '', note: '', when: 'now', day: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/balance`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the balance.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = () => {
    setForm({ amount: data?.balance != null ? String(data.balance) : '', note: '', when: 'now', day: firstOfMonth() });
    setFormError('');
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (String(form.amount).trim() === '' || !Number.isFinite(Number(form.amount))) {
      setFormError('Write the amount, like 2450');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/balance`,
        { amount: Number(form.amount), note: form.note, fromDay: form.when === 'day' && form.day ? form.day : null },
        auth()
      );
      setData(res.data);
      setEditing(false);
    } catch (err) {
      setFormError(err.response?.data?.error || 'We couldn’t save the balance.');
    } finally {
      setSaving(false);
    }
  };

  const s = data?.since;
  const series = data?.series || [];
  const up = s ? s.paid - s.expenses : 0;
  const lineData = {
    labels: series.map((x) => shortDay(x.day)),
    datasets: [
      {
        label: 'Balance',
        data: series.map((x) => x.balance),
        borderColor: c.s3,
        backgroundColor: c.s3,
        borderWidth: 2,
        pointRadius: series.length > 40 ? 0 : 3,
        tension: 0.25,
      },
    ],
  };
  const options = baseOptions(c, { money: true });
  options.plugins.legend = { display: false };

  return (
    <Panel
      className="rmbal"
      title={
        <>
          <FontAwesomeIcon icon={faBuildingColumns} /> Account balance
        </>
      }
      sub={
        data?.start
          ? `Follows by itself from ${startLabel(data.start)}: + packages paid, − expenses.`
          : 'Type what is in the account (now, or at the start of the month), and it follows by itself from there.'
      }
      actions={
        <div className="rmbal-actions">
          <button type="button" className="rmadd-icon-btn" onClick={load} aria-label="Refresh">
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
          <button type="button" className="rmadd-btn" onClick={open} disabled={!data && !error}>
            <FontAwesomeIcon icon={faPen} /> {data?.start ? 'Correct the balance' : 'Set the balance'}
          </button>
        </div>
      }
    >
      {error && <p className="rmbal-error">{error}</p>}
      {!data && !error && <div className="rmadd-chart rmadd-skel rmbal-skel" />}

      {data && !data.start && (
        <div className="rmbal-empty">
          <p>No balance yet. Check your account(s) now, and type the total.</p>
          <button type="button" className="rmadd-btn" onClick={open}>
            Set the balance
          </button>
        </div>
      )}

      {data?.start && (
        <div className="rmbal-body">
          <div className="rmbal-now">
            <span>Balance now</span>
            <strong className={data.balance < 0 ? 'is-bad' : ''}>{money(data.balance)}</strong>
            <ul>
              <li>
                <span>Started with</span>
                <b>{money(data.start.amount)}</b>
              </li>
              <li>
                <span>
                  + Packages paid <i>× {s.paidCount}</i>
                </span>
                <b className="is-good">+{money(s.paid)}</b>
              </li>
              <li>
                <span>− Teachers</span>
                <b>−{money(s.byKind.teachers)}</b>
              </li>
              <li>
                <span>− Business costs</span>
                <b>−{money(s.byKind.business)}</b>
              </li>
              <li>
                <span>− Personal</span>
                <b>−{money(s.byKind.personal)}</b>
              </li>
              <li className="is-total">
                <span>Since the start</span>
                <b className={up < 0 ? 'is-bad' : 'is-good'}>
                  {up >= 0 ? '+' : '−'}
                  {money(Math.abs(up))}
                </b>
              </li>
            </ul>
            {data.start.note && <p className="rmbal-note">“{data.start.note}”</p>}
          </div>
          {chart && (
            <div className="rmbal-chart">
              {series.length > 1 ? (
                <Line data={lineData} options={options} />
              ) : (
                <p className="rmbal-hint">The balance line shows up from tomorrow, day by day.</p>
              )}
            </div>
          )}
        </div>
      )}

      {editing && (
        <DashModal
          title={data?.start ? 'Correct the balance' : 'Set the balance'}
          kicker="Account balance"
          onClose={() => setEditing(false)}
          busy={saving}
          footer={
            <>
              <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" form="rmbal-form" className="rmadd-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="rmbal-form" className="rmadd-form" onSubmit={save} noValidate>
            <div className="rmadd-field">
              <span>This amount was in the account</span>
              <div className="rmbal-when">
                <label className={form.when === 'now' ? 'is-on' : ''}>
                  <input type="radio" name="rmbal-when" checked={form.when === 'now'} onChange={() => setForm((f) => ({ ...f, when: 'now' }))} />
                  Right now
                </label>
                <label className={form.when === 'day' ? 'is-on' : ''}>
                  <input type="radio" name="rmbal-when" checked={form.when === 'day'} onChange={() => setForm((f) => ({ ...f, when: 'day' }))} />
                  At the start of a day
                </label>
              </div>
              {form.when === 'day' && (
                <input
                  type="date"
                  className="rmadd-input"
                  max={todayISO()}
                  value={form.day}
                  onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                  aria-label="The day"
                />
              )}
            </div>
            <label className="rmadd-field">
              <span>{form.when === 'day' && form.day ? `In the account at the start of ${longDay(form.day)} ($)` : 'What is in the account right now ($)'}</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className="rmadd-input"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                autoFocus
              />
            </label>
            <label className="rmadd-field">
              <span>Note (optional)</span>
              <input
                type="text"
                className="rmadd-input"
                placeholder="e.g. Whish + bank + cash"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            </label>
            <p className="rmbal-hint">
              {form.when === 'day'
                ? 'From that day it follows by itself: + every package paid from that day, − every expense dated from that day (teachers, costs, personal). '
                : 'From now on it follows by itself: + every package you mark paid, − every expense you add (teachers, costs, personal). Packages and expenses from before now are already in this number. '}
              If it drifts from the real account, correct it here any time.
            </p>
            {formError && <p className="rmbal-error">{formError}</p>}
          </form>
          {data?.history?.length > 1 && (
            <div className="rmbal-history">
              <p>Before</p>
              <ul>
                {data.history.slice(1, 6).map((h) => (
                  <li key={h.id}>
                    <span>{startLabel(h)}</span>
                    <b>{money(h.amount)}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DashModal>
      )}
    </Panel>
  );
};

export default BalanceCard;
